import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box, Table, TableBody, TableCell,
    TableContainer, TableFooter, TableHead,
    TableRow, Typography, IconButton, Checkbox, Tooltip, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {getAdjustmentTotal, getManualAdjustmentTotal, getAutoAdjustmentFromRow} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import {DownloadSelection, InputField, MoneyField, UpDownIconDisplay} from '@/components/common';
import {toNumber, computeCommonTotals, formatFooterAmount} from '@/utils/dataUtils';
import {computeDividendPayableTotals, isDividendPayableCode} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/dividendPayableSchedule';
import {CommonRow} from "@/services/auditService/interface/wp-row";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import {SxProps} from "@mui/system";
import {Theme} from "@mui/material/styles";
import {AdjustmentCell} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/AdjustmentCell';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import AttachmentIconButton from '@/adjustment/adjust/support/AttachmentIconButton';
import type { AttachmentPreviewConfig } from '@/adjustment/adjust/support/attachmentColUtils';
import {WpNotesAnchor} from '@/adjustment/adjust/support/WpNotesContext';
import {PRIOR_YEAR_DIVIDER_SX} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/priorYearDividerSx';
import {placeholderRowIdAtIndex, placeholderRowIdForAppend} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpPlaceholderRowId';

export interface ColProperties {
    title?: string;
    editable?: boolean
    type: 'text' | 'number' | 'money' | 'button' | 'render';
    defaultValue?: any | ((row: CommonRow, rowIndex: number) => any)
    sx?: SxProps<Theme>;
    buttonIcon?: React.ReactNode;  // 按钮图标
    onClick?: (row: CommonRow, rowIndex: number) => void; // 点击回调
    renderCell?: (row: CommonRow, rowIndex: number) => React.ReactNode; // 自定义渲染
    attachmentPreview?: AttachmentPreviewConfig;
}

// Row error state type
export type RowErrors = Record<number, {
    item?: string;
    perClient?: string;
    commissionPaymentName?: string;
    [key: string]: string | undefined
}>;

export type CommonTableProps = {
    rows: CommonRow[];
    onRowsChange: (rows: CommonRow[]) => void;
    yoa: string | null;
    lastYoa: string | null;
    currency: string;
    code?: string;
    onItemClick?: (row: CommonRow, rowIndex: number) => void;
    // onAuditConfExport?: (items: string[]) => Promise<void> | void;
    onAuditConfExport?: (data: {
    items: string[];
    rowIds: number[];
    amounts: number[];
    code: string | null;
    }) => Promise<void> | void;
    // Optional: when combineRule is provided, emit totals.final to parent
    onFinalChange?: (final: number) => void;
    // Optional: mark Total Final (current year) red when false
    currentPer?: boolean;
    // Optional: mark Total Final (last year) red when false
    lastYearPer?: boolean;
    // If undefined, defaults to true (allow add/delete)
    allowAddRow?: boolean;
    appendCol?: ColProperties[];
    // If true, adjustment column is readOnly
    adjustmentReadOnly?: boolean;
    // Per-row adjustment readOnly overrides (row IDs in this set will have adjustment readonly)
    adjustmentReadOnlyRowIds?: Set<number>;
    /** Optional parent-supplied pending values merged into display (e.g. Tax payable mirror). */
    supplementalPendingAdjustments?: Record<number, number>;
    // If true, all fields are read-only (display mode)
    readOnly?: boolean;
    // Callback when an individual adjustment's adjustHeaderId is clicked (opens AdjustTabsDialog)
    onAdjustmentCellClick?: (adjustHeaderId: string, row: CommonRow) => void;
    // Callback to initiate a new adjustment from the Drawer (wp ??selectionDrawer ??adjustTabDialog)
    onNewAdjustment?: (row: CommonRow) => void;
    // Callback to open the adjustment details drawer at the parent level (headerIds collected from row.adjustments)
    onAdjustmentDrawerOpen?: (headerIds: string[], itemName: string) => void;
    // Row-level validation errors
    rowErrors?: RowErrors;
    totalTitle?:string;
    isItemEditable?: (row: CommonRow, rowIndex: number) => boolean;
    isPerClientEditable?: (row: CommonRow, rowIndex: number) => boolean;
    isLastYearFinalEditable?: (row: CommonRow, rowIndex: number) => boolean;
    /** 控制每行是否显示删除按钮，默认 true */
    isRowDeletable?: (row: CommonRow, rowIndex: number) => boolean;
    /** Callback when a row enters "add new adjustment" mode and value changes */
    onNewAdjustmentPending?: (pendingAdjustments: Record<number, number>) => void;
    /** Optional per-row adjustment sign normalization (caller-specific, e.g. TaxWp). */
    normalizeAdjustmentInput?: (row: CommonRow, rowIndex: number, value: number) => number;
    totalRowAction?: React.ReactNode;   // 合计行右侧的自定义操作
    /** 是否展开 adjustment 列为三子列（Manual Adj / Manual Final / Auto Adj） */
    adjustmentExpanded?: boolean;
    /**
     * Optional Auto Adj total-row value when expanded (opt-in).
     * When omitted, footer Auto Adj cell stays blank — legacy CommonTable behavior.
     */
    autoAdjTotalOverride?: number;
    /** 是否隐藏 Last Year Final 和 change 列（首次审计时隐藏） */
    hideLastYearColumn?: boolean;
    /** Override the total row's lastYearFinal display value (instead of computed sum) */
    totalLastYearFinal?: number;
    /** When true, hide the footer total row (caller renders summary as a data row instead) */
    hideTotalFooter?: boolean;

    /** Whether to show EditIcon on Final column header and support Split mode */
    finalSplitModeEnabled?: boolean;
    /** Whether column-level Split mode is active */
    finalSplitModeActive?: boolean;
    /** Toggle Split mode for Final column */
    onFinalSplitModeToggle?: () => void;
    /**
     * DIR/DTD only: column-level Edit/Cancel on header (no per-row cancel icons).
     * Display mode always shows calculated Final; split mode shows calculated → editable override.
     */
    finalSplitColumnControl?: boolean;
    /** Custom per-row calculated Final (e.g. includes autoAdj) */
    getRowCalculatedFinal?: (row: CommonRow) => number;
    /** Read finalChange override from row data */
    getRowFinalChange?: (row: CommonRow) => number | null | undefined;
    /** Update finalChange override for a row */
    onRowFinalChange?: (rowId: number, value: number | null) => void;
    /** Final column display value (includes override when set) */
    getFinalColumnDisplay?: (row: CommonRow) => number;
    /** Optional custom item cell; return undefined to use default rendering */
    renderItemCell?: (row: CommonRow, rowIndex: number) => React.ReactNode | undefined;
    /** Optional custom Per client cell; return undefined to use default MoneyField */
    renderPerClientCell?: (row: CommonRow, rowIndex: number) => React.ReactNode | undefined;
    /** Override Per client column header label (default: "Per client") */
    perClientColumnTitle?: string;
    /** When set, show a cancel icon after each Final value (e.g. close inline field-adjust panel) */
    onFinalCancel?: () => void;

};

export function CommonTable({
                                rows, onRowsChange, yoa, lastYoa, currency, code,
                                onItemClick, onAuditConfExport, onFinalChange, currentPer,
                                lastYearPer, allowAddRow = true, appendCol,
                                adjustmentReadOnly = false, adjustmentReadOnlyRowIds, supplementalPendingAdjustments, readOnly = false,
                                onAdjustmentCellClick, onNewAdjustment, rowErrors = {}, onAdjustmentDrawerOpen,
                                totalTitle = 'Total', isItemEditable,                                 isPerClientEditable, isLastYearFinalEditable, isRowDeletable, onNewAdjustmentPending,totalRowAction,
                                normalizeAdjustmentInput,
                                adjustmentExpanded = false,
                                autoAdjTotalOverride,
                                hideLastYearColumn = false,
                                totalLastYearFinal,
                                hideTotalFooter = false,
                                finalSplitModeEnabled = false,
                                finalSplitModeActive = false,
                                onFinalSplitModeToggle,
                                finalSplitColumnControl = false,
                                getRowCalculatedFinal,
                                getRowFinalChange,
                                onRowFinalChange,
                                getFinalColumnDisplay,
                                renderItemCell,
                                renderPerClientCell,
                                perClientColumnTitle = 'Per client',
                                onFinalCancel,
                            }: CommonTableProps) {
    const isFirstAudit = useCompanyAnnualStore(s => s.isCurrentFirstAudit());
    const effectiveHideLastYear = hideLastYearColumn || isFirstAudit;

    // When readOnly is true, disable all editing
    const effectiveAllowAddRow = readOnly ? false : allowAddRow;
    const effectiveAdjustmentReadOnly = readOnly ? true : adjustmentReadOnly;

    /** DIR/DTD Final override feature — only active when caller passes split/display props */
    const hasFinalSplitFeature = finalSplitModeEnabled || !!getFinalColumnDisplay
        || (finalSplitColumnControl && !!getRowCalculatedFinal && !!onRowFinalChange);

    const auditConfCodes = useMemo(() => ([
        'BS-CA-ORC', 'BS-CA-DPP', 'BS-CA-PPM', 'BS-CA-TR', 'BS-CA-DIMH', 'BS-CA-DSS',
        'BS-CA-DRP', 'BS-CA-DRP2', 'BS-CA-DRP3', 'BS-CL-TP', 'BS-CL-OP', 'BS-CL-DR', 'BS-CL-ACC', 'BS-CL-BOD',
        'BS-CL-DTS', 'BS-CL-DTSH', 'BS-CL-DTRC', 'BS-CL-DRP', 'BS-CL-DRP2', 'BS-CL-DRP3',
        'BS-CA-DIR', 'BS-CL-DTD',
    ]), []);

    const enableAuditConfExport = !!code && auditConfCodes.includes(code);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set());
    const [exporting, setExporting] = useState(false);
    const [exportingRowKey, setExportingRowKey] = useState<string | null>(null);

    const [downloadPromptOpen, setDownloadPromptOpen] = useState(false);
    const [downloadPromptRowKey, setDownloadPromptRowKey] = useState<string | null>(null);
    const [multiSelectMode, setMultiSelectMode] = useState(false);

    // State for tracking rows in "add new adjustment" mode
    const [addingNewRows, setAddingNewRows] = useState<Set<number>>(new Set());
    const [pendingAdjustments, setPendingAdjustments] = useState<Record<number, number>>({});

    // Notify parent when pending adjustments change
    // Use ref to avoid infinite loops when parent's callback changes
    const onNewAdjustmentPendingRef = React.useRef(onNewAdjustmentPending);
    React.useEffect(() => {
        onNewAdjustmentPendingRef.current = onNewAdjustmentPending;
    }, [onNewAdjustmentPending]);
    
    useEffect(() => {
        onNewAdjustmentPendingRef.current?.(pendingAdjustments);
    }, [pendingAdjustments]);

    const handleNewAdjustmentValueChange = useCallback((rowId: number | null | undefined, value: number | null) => {
        if (rowId == null) return;
        if (value === null) {
            // Cancel - remove from pending and adding state
            setAddingNewRows(prev => {
                const next = new Set(prev);
                next.delete(rowId);
                return next;
            });
            setPendingAdjustments(prev => {
                const next = {...prev};
                delete next[rowId];
                return next;
            });
        } else {
            // Add/Update pending adjustment
            setAddingNewRows(prev => new Set(prev).add(rowId));
            setPendingAdjustments(prev => ({...prev, [rowId]: value}));
        }
    }, []);

    // Local state to hold values for dynamically appended columns (per row)
    const [appendValues, setAppendValues] = useState<Record<string, any[]>>({});
    const setAppendValue = (rowKey: string, colIdx: number, val: any) => {
        setAppendValues((prev) => {
            const next = {...prev};
            const arr = [...(next[rowKey] || [])];
            arr[colIdx] = val;
            next[rowKey] = arr;
            return next;
        });
    };

    // Inner child component for rendering a single appended column cell
    const AppendColCell: React.FC<{
        rowKey: string;
        colIdx: number;
        col: ColProperties;
        value: any;
        row: CommonRow;
        rowIndex: number;
        currency: string;
        onChange: (val: any) => void;
    }> = ({rowKey, colIdx, col, value, currency,row, rowIndex, onChange}) => {
        const editable = readOnly ? false : (col.editable ?? true);
        const type = col.type;
        if (col.type === 'render' && col.renderCell) {
            return (
                <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                    {col.renderCell(row, rowIndex)}
                </TableCell>
            );
        }
         if (col.type === 'button') {
            if (col.attachmentPreview) {
                const targetId = col.attachmentPreview.getTargetId?.(row, rowIndex) ?? row.id ?? 0;
                return (
                    <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                        <AttachmentIconButton
                            targetType={col.attachmentPreview.targetType}
                            targetId={targetId}
                            companyId={col.attachmentPreview.companyId}
                            annualId={col.attachmentPreview.annualId}
                            onClick={() => col.onClick?.(row, rowIndex)}
                        />
                    </TableCell>
                );
            }
            return (
                <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                    <IconButton size="small" onClick={() => col.onClick?.(row, rowIndex)}>
                        {col.buttonIcon}
                    </IconButton>
                </TableCell>
            );
        }
        if (type === 'text') {
            return (
                <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                    {editable ? (
                        <InputField
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="text"
                            align="left"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    ) : (
                        <Typography variant="body2">{String(value ?? '')}</Typography>
                    )}
                </TableCell>
            );
        }
        if (type === 'number') {
            return (
                <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                    <InputField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                        align="right"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        display={!editable}
                    />
                </TableCell>
            );
        }
        // money
        return (
            <TableCell key={`append-${rowKey}-${colIdx}`} align="center" sx={col.sx}>
                <MoneyField
                    value={Number(value) ?? 0}
                    showCents
                    align="center"
                    compact
                    currencySymbol={currency}
                    editable={editable}
                    onChangeNumber={(n) => onChange(n)}
                />
            </TableCell>
        );
    };

    const openAdjustmentDrawer = useCallback((row: CommonRow) => {
        if (onAdjustmentDrawerOpen) {
            const ids = (row.adjustments || []).map(a => a.adjustHeaderId).filter((id): id is string => !!id);
            if (ids.length > 0) onAdjustmentDrawerOpen(ids, row.item || '');
        }
    }, [onAdjustmentDrawerOpen]);

    const totals = useMemo(() => {
        const base = isDividendPayableCode(code)
            ? computeDividendPayableTotals(rows)
            : computeCommonTotals(rows);
        // Add pending adjustments to final
        const pendingTotal = Object.values(pendingAdjustments).reduce((sum, val) => sum + val, 0)
            + Object.values(supplementalPendingAdjustments || {}).reduce((sum, val) => sum + val, 0);
        const autoAdjSum = (rows || []).reduce((sum, row) => sum + getAutoAdjustmentFromRow(row), 0);
        return {
            ...base,
            autoAdj: autoAdjSum,
            final: base.final + pendingTotal
        };
    }, [rows, pendingAdjustments, supplementalPendingAdjustments, code]);

    const resolvedAutoAdjTotal = useMemo(() => {
        if (autoAdjTotalOverride != null) return Number(autoAdjTotalOverride) || 0;
        return totals.autoAdj ?? 0;
    }, [autoAdjTotalOverride, totals.autoAdj]);

    // Parenthesis totals / Manual+Auto period Final: DIR-only (finalSplitColumnControl / autoAdj override / expanded).
    const useDirExpandedTotals = Boolean(
        finalSplitColumnControl || autoAdjTotalOverride != null || adjustmentExpanded,
    );

    /**
     * Calculated Final total (excludes finalChange overrides).
     * DIR/DTD passes getRowCalculatedFinal = Manual Final; Total must never sum finalChange.
     */
    const calculatedFinalTotal = useMemo(() => {
        if (!getRowCalculatedFinal) return null;
        return (rows || []).reduce((sum, row) => sum + getRowCalculatedFinal(row), 0);
    }, [rows, getRowCalculatedFinal]);

    const finalColumnTotal = useMemo(() => {
        // Prefer calculated Final — never sum finalChange / override display values into Total
        if (calculatedFinalTotal != null) {
            return calculatedFinalTotal;
        }
        if (!hasFinalSplitFeature || !getFinalColumnDisplay) {
            return totals.final;
        }
        return (rows || []).reduce((sum, row) => sum + getFinalColumnDisplay(row), 0);
    }, [rows, getFinalColumnDisplay, totals.final, hasFinalSplitFeature, calculatedFinalTotal]);

    /** Legacy Final total (pre DIR autoAdj work). */
    const legacyFinalTotal = hasFinalSplitFeature ? finalColumnTotal : totals.final;

    /** Period-year Final: DIR expanded = Manual Final only (exclude finalChange and Auto Adj). */
    const periodFinalTotal = useMemo(() => {
        if (!useDirExpandedTotals) {
            return legacyFinalTotal;
        }
        if (!adjustmentExpanded) {
            return legacyFinalTotal;
        }
        if (calculatedFinalTotal != null) {
            return calculatedFinalTotal;
        }
        return (rows || []).reduce((sum, row) => {
            const pending = (row.id != null ? (pendingAdjustments[row.id] || 0) : 0)
                + (row.id != null ? ((supplementalPendingAdjustments || {})[row.id] || 0) : 0);
            return sum + toNumber(row.perClient) + getManualAdjustmentTotal(row) + pending;
        }, 0);
    }, [
        useDirExpandedTotals,
        adjustmentExpanded,
        calculatedFinalTotal,
        legacyFinalTotal,
        rows,
        pendingAdjustments,
        supplementalPendingAdjustments,
    ]);

    useEffect(() => {
        onFinalChange?.(totals.final);
    }, [onFinalChange, totals.final])

    const addRow = () => {
        onRowsChange([
            ...rows,
            {
                id: placeholderRowIdForAppend(rows.length),
                item: '',
                itemEdit: true,
                perClient: undefined,
                finalAmount: undefined,
                lastYearFinal: undefined
            },
        ]);
    };

    const removeRow = (id: number | null | undefined, rowIndex?: number) => {
        if (id != null) {
            onRowsChange(rows.filter((r) => r.id !== id));
            return;
        }
        // Placeholder rows may lack id — fall back to index
        if (rowIndex == null || rowIndex < 0 || rowIndex >= rows.length) return;
        onRowsChange(rows.filter((_, i) => i !== rowIndex));
    };

    const updateRow = (id: number | null | undefined, patch: Partial<CommonRow>, rowIndex?: number) => {
        if (id != null) {
            onRowsChange(rows.map((r) => (r.id === id ? {...r, ...patch} : r)));
            return;
        }
        // Null id (backend placeholder): stamp index-based temp id for pendingAdjustments / attr bind.
        if (rowIndex == null || rowIndex < 0 || rowIndex >= rows.length) return;
        onRowsChange(rows.map((r, i) =>
            i === rowIndex ? {...r, ...patch, id: placeholderRowIdAtIndex(rowIndex)} : r
        ));
    };

    const toggleSelectRow = (rowKey: string, checked: boolean) => {
        setSelectedRowKeys((prev) => {
            const next = new Set(prev);
            if (checked) next.add(rowKey);
            else next.delete(rowKey);
            return next;
        });
    };

    const exportableRowKeys = useMemo(() => {
        const keys: string[] = [];
        (rows || []).forEach((r, idx) => {
            const k = r.id !== null && r.id !== undefined ? String(r.id) : `row-${idx}`;
            const item = String((r as any)?.item || '').trim();
            if (!item) return;
            keys.push(k);
        });
        return keys;
    }, [rows]);

    const exportableRowCount = exportableRowKeys.length;

    useEffect(() => {
        if (exportableRowCount <= 1 && multiSelectMode) {
            setMultiSelectMode(false);
            setSelectedRowKeys(new Set());
        }
    }, [exportableRowCount, multiSelectMode]);

    const getSelectedItems = (rowKeyFilter?: string) => {
        const items: string[] = [];
        const rowIds: number[] = [];
        const amounts: number[] = [];
        let codeValue: string | null = null;
        (rows || []).forEach((r, idx) => {
            const k = r.id !== null && r.id !== undefined ? String(r.id) : `row-${idx}`;
            const shouldTake = rowKeyFilter ? k === rowKeyFilter : selectedRowKeys.has(k);
            if (!shouldTake) return;
            const item = String((r as any)?.item || '').trim();
            if (!item) return;
            items.push(item);

            // Keep rowIds aligned with items (0 = missing id; backend falls back by item name)
            const rowId = r.id != null && Number(r.id) > 0 ? Number(r.id) : 0;
            rowIds.push(rowId);

            const adjTotal = getAdjustmentTotal(r);
            const pendingAdj = (r.id != null && (
                pendingAdjustments[r.id]
                ?? supplementalPendingAdjustments?.[r.id]
            )) || 0;
            // Same Final formula as the table display row
            amounts.push(toNumber(r.perClient) + adjTotal + pendingAdj);

            if (!codeValue && r.code) codeValue = r.code;
        });
        return { items, rowIds, amounts, code: codeValue };
    };

    const handleExportSelected = async (args?: { exitMultiSelectAfter?: boolean }) => {
        if (!enableAuditConfExport || exporting || !onAuditConfExport) return;
        const { items, rowIds, amounts, code } = getSelectedItems();
        if (!items.length) return;

        let exportSucceeded = false;
        try {
            setExporting(true);
            setExportingRowKey(null);
            await onAuditConfExport({ items, rowIds, amounts, code });
            exportSucceeded = true;
        } finally {
            setExporting(false);
            setExportingRowKey(null);

            if (args?.exitMultiSelectAfter && exportSucceeded) {
                setMultiSelectMode(false);
                setSelectedRowKeys(new Set());
            }
        }
    };

    const handleExportRow = async (rowKey: string) => {
        if (!enableAuditConfExport || exporting || !onAuditConfExport) return;
        const { items, rowIds, amounts, code } = getSelectedItems(rowKey);
        if (!items.length) return;
        try {
            setExporting(true);
            setExportingRowKey(rowKey);
            await onAuditConfExport({ items, rowIds, amounts, code });
        } finally {
            setExporting(false);
            setExportingRowKey(null);
        }
    };

    const openDownloadPrompt = (rowKey: string) => {
        if (exporting) return;
        setDownloadPromptRowKey(rowKey);
        setDownloadPromptOpen(true);
    };

    const handleRowDownloadClick = (rowKey: string) => {
        if (exporting) return;

        // If only one exportable row exists, directly single download.
        if (exportableRowCount <= 1) {
            handleExportRow(rowKey);
            return;
        }

        openDownloadPrompt(rowKey);
    };

    const handlePromptSingleDownload = () => {
        const rk = downloadPromptRowKey;
        setDownloadPromptOpen(false);
        setDownloadPromptRowKey(null);
        if (!rk) return;
        handleExportRow(rk);
    };

    const handlePromptMultipleDownload = () => {
        const rk = downloadPromptRowKey;
        setDownloadPromptOpen(false);
        setDownloadPromptRowKey(null);
        setMultiSelectMode(true);

        if (exportableRowCount === 2) {
            setSelectedRowKeys(new Set(exportableRowKeys));
            return;
        }

        if (!rk) {
            setSelectedRowKeys(new Set());
            return;
        }
        setSelectedRowKeys(new Set([rk]));
    };

    return (
        <Box>
            <WpNotesAnchor/>
            <TableContainer component={Box}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {/* Blank title column has no YOA */}
                        <TableCell/>

                        {/* Dynamic appended columns YOA placeholders */}
                        {(appendCol || []).map((_, idx) => (
                            <TableCell key={`yoa-append-${idx}`} align="center"/>
                        ))}

                        {/* YOA over Per Client, Adjustment (or 3 sub-cols), Final (current) */}
                        <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                        {adjustmentExpanded ? (
                            <>
                                <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                                <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                                <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                            </>
                        ) : (
                            <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                        )}
                        <TableCell align="center">{yoa ? `(${yoa})` : ''}</TableCell>
                        {/* Last YOA over Final (last year) */}
                        {!effectiveHideLastYear && (
                            <TableCell align="center" sx={PRIOR_YEAR_DIVIDER_SX}>
                                {lastYoa ? `(${lastYoa})` : ''}
                            </TableCell>
                        )}
                        {/* Change and Action */}
                        {!effectiveHideLastYear && <TableCell align="center"/>}
                        {enableAuditConfExport && (
                            <TableCell align="center"/>
                        )}
                        <TableCell align="center"/>
                    </TableRow>
                </TableHead>

                {/* Column headers */}
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{minWidth: 220}}/>
                        {(appendCol || []).map((acol, idx) => (
                            <TableCell key={`head-append-${idx}`} align="center">{acol.title ?? ''}</TableCell>
                        ))}
                        <TableCell align="center">{perClientColumnTitle}</TableCell>
                        {adjustmentExpanded ? (
                            <>
                                <TableCell align="center" sx={{minWidth: 140}}>Manual Adj</TableCell>
                                <TableCell align="center" sx={{minWidth: 130}}>Manual Final</TableCell>
                                <TableCell align="center" sx={{minWidth: 130}}>Auto Adj</TableCell>
                            </>
                        ) : (
                            <TableCell align="center">Adjustment</TableCell>
                        )}
                        <TableCell align="center">
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                                Final
                                {finalSplitColumnControl && onFinalSplitModeToggle && (
                                    <IconButton size="small" onClick={onFinalSplitModeToggle}>
                                        {finalSplitModeActive
                                            ? <CloseIcon fontSize="small"/>
                                            : <EditIcon fontSize="small"/>}
                                    </IconButton>
                                )}
                                {!finalSplitColumnControl && finalSplitModeEnabled && (
                                    <IconButton size="small" onClick={onFinalSplitModeToggle}>
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                )}
                            </Box>
                        </TableCell>
                        {!effectiveHideLastYear && (
                            <TableCell align="center" sx={PRIOR_YEAR_DIVIDER_SX}>Final</TableCell>
                        )}
                        {!effectiveHideLastYear && <TableCell align="center">change</TableCell>}
                        {enableAuditConfExport && (
                            <TableCell align="center" sx={{width: 20}}>
                                {multiSelectMode && (
                                    <Tooltip title="Download selected">
                                        <span>
                                            <IconButton size="small" color="primary"
                                                        onClick={() => handleExportSelected({exitMultiSelectAfter: true})}
                                                        disabled={exporting || !onAuditConfExport || selectedRowKeys.size === 0}>
                                                {exporting && exportingRowKey == null ? <CircularProgress size={16}/> :
                                                    <DownloadIcon fontSize="small"/>}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </TableCell>
                        )}
                        <TableCell align="center" sx={{width: 20}}>
                            {effectiveAllowAddRow && (
                                <IconButton size="small" color="primary" onClick={addRow}>
                                    <AddCircleOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody sx={{'& .MuiTableCell-root': {paddingTop: '1px', paddingBottom: '1px'}}}>
                    {(rows || []).map((row, idx) => {
                        const rowKey = row.id !== null && row.id !== undefined ? String(row.id) : `row-${idx}`;
                        const itemEditable = readOnly ? false : (isItemEditable ? isItemEditable(row, idx) : true);
                        const perClientEditable = readOnly ? false : (isPerClientEditable ? isPerClientEditable(row, idx) : true);
                        const lastYearFinalEditable = readOnly ? false : (isLastYearFinalEditable ? isLastYearFinalEditable(row, idx) : true);
                        const adjTotal = getAdjustmentTotal(row);
                        // Include pending adjustment in final calculation
                        const pendingAdj = (row.id != null && (
                            pendingAdjustments[row.id]
                            ?? supplementalPendingAdjustments?.[row.id]
                        )) || 0;
                        const final = toNumber(row.perClient) + adjTotal + pendingAdj;
                        const lastYearFinal = toNumber(row.lastYearFinal);
                        const hasMultiAdj = Array.isArray(row.adjustments) && row.adjustments.length > 0;
                        const changePct = lastYearFinal !== 0 ? ((final - lastYearFinal) / Math.abs(lastYearFinal)) * 100 : 0;
                        const customItemCell = renderItemCell?.(row, idx);
                        const rowNormalizeAdjustmentInput = normalizeAdjustmentInput
                            ? (value: number) => normalizeAdjustmentInput(row, idx, value)
                            : undefined;
                        return (
                            <TableRow key={rowKey} hover>
                                <TableCell align="left" sx={{minWidth: 220}}>
                                    {customItemCell ?? (
                                    readOnly ? (
                                        onItemClick && String(row.item || '').trim() ? (
                                            <Typography
                                                variant="body2"
                                                onClick={() => onItemClick(row, idx)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                }}
                                            >
                                                {row.item}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2">{row.item}</Typography>
                                        )
                                    ) : effectiveAllowAddRow ? (
                                        itemEditable ? (
                                            <InputField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                align="left"
                                                value={row.item}
                                                onChange={(e) => updateRow(row.id, {item: e.target.value}, idx)}
                                                error={!!rowErrors[row.id ?? -1]?.item}
                                                helperText={rowErrors[row.id ?? -1]?.item}
                                            />
                                        ) : (
                                            onItemClick && String(row.item || '').trim() ? (
                                                <Typography
                                                    variant="body2"
                                                    onClick={() => onItemClick(row, idx)}
                                                    sx={{cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, color: 'primary.main'}}
                                                >
                                                    {row.item}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" sx={{fontWeight: 600}}>{row.item}</Typography>
                                            )
                                        )
                                    ) : (row.itemEdit ?? true ? (
                                        itemEditable ? (
                                            <InputField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                align="left"
                                                value={row.item}
                                                onChange={(e) => updateRow(row.id, {item: e.target.value}, idx)}
                                                error={!!rowErrors[row.id ?? -1]?.item}
                                                helperText={rowErrors[row.id ?? -1]?.item}
                                            />
                                        ) : (
                                            <Typography variant="body2" sx={{fontWeight: 600}}>{row.item}</Typography>
                                        )
                                    ) : (
                                        <Typography variant="body2" sx={{fontWeight: 600}}>{row.item}</Typography>
                                    )
                                    ))}
                                </TableCell>
                                {(appendCol || []).map((acol, aidx) => {
                                    const type = acol.type;
                                    const defaultValue = typeof acol.defaultValue === 'function'
                                        ? acol.defaultValue(row, idx)
                                        : acol.defaultValue;
                                    const currentVal = (appendValues[rowKey]?.[aidx]) ?? (defaultValue ?? (type === 'money' || type === 'number' ? 0 : ''));
                                    return (
                                        <AppendColCell
                                            key={`append-${rowKey}-${aidx}`}
                                            rowKey={rowKey}
                                            colIdx={aidx}
                                            col={acol}
                                            value={currentVal}
                                            currency={currency}
                                            row={row}
                                            rowIndex={idx}
                                            onChange={(val) => setAppendValue(rowKey, aidx, val)}
                                        />
                                    );
                                })}
                                <TableCell align="center">
                                    {(() => {
                                        const customPerClient = renderPerClientCell?.(row, idx);
                                        if (customPerClient !== undefined) return customPerClient;
                                        return (
                                            <MoneyField
                                                value={row.perClient ?? 0}
                                                showCents
                                                align="center"
                                                compact
                                                currencySymbol={currency}
                                                editable={perClientEditable}
                                                onChangeNumber={(n) => updateRow(row.id, {perClient: n}, idx)}
                                                error={!!rowErrors[row.id ?? -1]?.perClient}
                                                helperText={rowErrors[row.id ?? -1]?.perClient}
                                            />
                                        );
                                    })()}
                                </TableCell>
                                {adjustmentExpanded ? (
                                    <>
                                        {/* Manual Adj */}
                                        <TableCell align="center">
                                            <AdjustmentCell
                                                row={row}
                                                currency={currency}
                                                readOnly={effectiveAdjustmentReadOnly || (adjustmentReadOnlyRowIds?.has(row.id ?? -1) ?? false)}
                                                normalizeAdjustmentInput={rowNormalizeAdjustmentInput}
                                                onAdjustmentChange={(n) => updateRow(row.id, {adjustment: n}, idx)}
                                                onOpenDrawer={() => openAdjustmentDrawer(row)}
                                                onNewAdjustmentValueChange={handleNewAdjustmentValueChange}
                                                isAddingNew={addingNewRows.has(row.id ?? -1)}
                                                setIsAddingNew={(adding) => {
                                                    if (row.id == null) return;
                                                    setAddingNewRows(prev => {
                                                        const next = new Set(prev);
                                                        if (adding) next.add(row.id!);
                                                        else next.delete(row.id!);
                                                        return next;
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                        {/* Manual Final = perClient + manualAdj + pendingAdj */}
                                        <TableCell align="center">
                                            <MoneyField
                                                value={toNumber(row.perClient) + getManualAdjustmentTotal(row) + pendingAdj}
                                                showCents align="center" compact currencySymbol={currency}
                                            />
                                        </TableCell>
                                        {/* Auto Adj */}
                                        <TableCell align="center">
                                            <MoneyField
                                                value={getAutoAdjustmentFromRow(row)}
                                                showCents align="center" compact currencySymbol={currency}
                                            />
                                        </TableCell>
                                    </>
                                ) : (
                                    <TableCell align="center">
                                        <AdjustmentCell
                                            row={row}
                                            currency={currency}
                                            readOnly={effectiveAdjustmentReadOnly || (adjustmentReadOnlyRowIds?.has(row.id ?? -1) ?? false)}
                                            normalizeAdjustmentInput={rowNormalizeAdjustmentInput}
                                            onAdjustmentChange={(n) => updateRow(row.id, {adjustment: n}, idx)}
                                            onOpenDrawer={() => openAdjustmentDrawer(row)}
                                            onNewAdjustmentValueChange={handleNewAdjustmentValueChange}
                                            isAddingNew={addingNewRows.has(row.id ?? -1)}
                                            setIsAddingNew={(adding) => {
                                                if (row.id == null) return;
                                                setAddingNewRows(prev => {
                                                    const next = new Set(prev);
                                                    if (adding) next.add(row.id!);
                                                    else next.delete(row.id!);
                                                    return next;
                                                });
                                            }}
                                        />
                                    </TableCell>
                                )}
                                <TableCell align="center">
                                    {!hasFinalSplitFeature ? (
                                        <Box sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.25}}>
                                            <MoneyField
                                                value={adjustmentExpanded
                                                    ? (toNumber(row.perClient) + getManualAdjustmentTotal(row) + pendingAdj + getAutoAdjustmentFromRow(row))
                                                    : final}
                                                showCents align="center" compact currencySymbol={currency}
                                            />
                                            {onFinalCancel && (
                                                <Tooltip title="Cancel">
                                                    <IconButton size="small" onClick={onFinalCancel} aria-label="Cancel">
                                                        <CloseIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : (() => {
                                        const calculatedFinal = getRowCalculatedFinal?.(row) ?? (adjustmentExpanded
                                            ? (toNumber(row.perClient) + getManualAdjustmentTotal(row) + pendingAdj + getAutoAdjustmentFromRow(row))
                                            : final);
                                        const finalChange = getRowFinalChange?.(row);
                                        // column-control (DIR): null = no override; 0 is a valid override.
                                        // legacy per-row edit: only non-zero overrides are meaningful.
                                        const hasFinalChangeOverride = finalSplitColumnControl
                                            ? finalChange != null
                                            : (finalChange != null && Math.abs(Number(finalChange)) > 0.001);
                                        const suffixDisplay = hasFinalChangeOverride
                                            ? Number(finalChange)
                                            : calculatedFinal;

                                        // Split mode: MoneyField with editable=false renders left (calculated) + right (suffix input) together
                                        if (finalSplitModeActive && onRowFinalChange && row.id != null) {
                                            const canEditSuffix = finalSplitColumnControl || finalSplitModeEnabled;
                                            return (
                                                <MoneyField
                                                    value={calculatedFinal}
                                                    separator="→"
                                                    suffixValue={suffixDisplay}
                                                    forceShowSplitMode={true}
                                                    cancelIcon={!finalSplitColumnControl && finalSplitModeEnabled
                                                        ? <CloseIcon fontSize="small"/>
                                                        : undefined}
                                                    onSuffixChange={canEditSuffix ? (n) => {
                                                        if (finalSplitColumnControl) {
                                                            // DIR-only: persist typed value as-is (0 is a valid override).
                                                            onRowFinalChange(row.id!, n);
                                                            return;
                                                        }
                                                        // Legacy per-row split: matching calculated clears override.
                                                        if (Math.abs(n - calculatedFinal) < 0.01) {
                                                            onRowFinalChange(row.id!, null);
                                                        } else {
                                                            onRowFinalChange(row.id!, n);
                                                        }
                                                    } : undefined}
                                                    onEditCancel={!finalSplitColumnControl && finalSplitModeEnabled
                                                        ? () => onRowFinalChange(row.id!, null)
                                                        : undefined}
                                                    showCents
                                                    align="center"
                                                    compact
                                                    currencySymbol={currency}
                                                />
                                            );
                                        }

                                        const displayFinal = finalSplitColumnControl
                                            ? calculatedFinal
                                            : (getFinalColumnDisplay?.(row) ?? calculatedFinal);
                                        return (
                                            <MoneyField
                                                value={displayFinal}
                                                showCents
                                                align="center"
                                                compact
                                                currencySymbol={currency}
                                            />
                                        );
                                    })()}
                                </TableCell>
                                {!effectiveHideLastYear && (
                                    <TableCell align="center" sx={PRIOR_YEAR_DIVIDER_SX}>
                                        <MoneyField
                                            value={row.lastYearFinal ?? 0}
                                            showCents
                                            align="center"
                                            compact
                                            currencySymbol={currency}
                                            editable={lastYearFinalEditable}
                                            onChangeNumber={(n) => updateRow(row.id, {lastYearFinal: n}, idx)}
                                        />
                                    </TableCell>
                                )}
                                {!effectiveHideLastYear && (
                                    <TableCell align="center">
                                        <UpDownIconDisplay changePercent={changePct}/>
                                    </TableCell>
                                )}
                                {enableAuditConfExport && (
                                    <TableCell align="center" sx={{width: 20}}>
                                        {multiSelectMode ? (
                                            <Checkbox
                                                size="small"
                                                checked={selectedRowKeys.has(rowKey)}
                                                disabled={!String(row.item || '').trim()}
                                                onChange={(e) => toggleSelectRow(rowKey, e.target.checked)}
                                            />
                                        ) : (
                                            <Tooltip title="Download">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleRowDownloadClick(rowKey)}
                                                        disabled={exporting || !onAuditConfExport || !String(row.item || '').trim()}
                                                    >
                                                        {exportingRowKey === rowKey ? <CircularProgress size={16}/> :
                                                            <DownloadIcon fontSize="small"/>}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align="center" sx={{width: 20}}>
                                    {effectiveAllowAddRow && (isRowDeletable ? isRowDeletable(row, idx) : true) && (
                                        <IconButton size="small" color="error" onClick={() => removeRow(row.id, idx)}>
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>

                {!hideTotalFooter && (
                <TableFooter>
                    <TableRow>
                        <TableCell align="right">
                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                {totalTitle}
                            </Typography>
                        </TableCell>
                        {(appendCol || []).map((_, idx) => (
                            <TableCell key={`foot-append-${idx}`}/>
                        ))}
                         {totalRowAction && (
                            <TableCell align="center" sx={{ width: 20 }}>
                                {totalRowAction}
                            </TableCell>
                        )}
                        <TableCell align="center">
                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                {formatFooterAmount(totals.perClient, useDirExpandedTotals)}
                            </Typography>
                        </TableCell>
                        {adjustmentExpanded ? (
                            <>
                                <TableCell align="center">
                                    <Typography variant="body1" sx={{fontWeight: 600}}>
                                        {formatFooterAmount(totals.adjustment, useDirExpandedTotals)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" />
                                <TableCell align="center">
                                    {autoAdjTotalOverride != null ? (
                                        <Typography variant="body1" sx={{fontWeight: 600}}>
                                            {formatFooterAmount(resolvedAutoAdjTotal, useDirExpandedTotals)}
                                        </Typography>
                                    ) : null}
                                </TableCell>
                            </>
                        ) : (
                            <TableCell align="center">
                                <Typography variant="body1" sx={{fontWeight: 600}}>
                                    {formatFooterAmount(totals.adjustment, useDirExpandedTotals)}
                                </Typography>
                            </TableCell>
                        )}
                        <TableCell align="center">
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: currentPer === false ? 'error.main' : 'inherit',
                                }}
                            >
                                {formatFooterAmount(periodFinalTotal, useDirExpandedTotals)}
                            </Typography>
                        </TableCell>
                        {!effectiveHideLastYear && (
                            <TableCell align="center" sx={PRIOR_YEAR_DIVIDER_SX}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: lastYearPer === false ? 'error.main' : 'inherit',
                                    }}
                                >
                                    {formatFooterAmount(totalLastYearFinal ?? totals.lastYearFinal, useDirExpandedTotals)}
                                </Typography>
                            </TableCell>
                        )}
                        {!effectiveHideLastYear && <TableCell/>}
                        {enableAuditConfExport && (
                            <TableCell/>
                        )}
                        <TableCell/>
                    </TableRow>
                </TableFooter>
                )}
            </Table>
            <DownloadSelection
                open={downloadPromptOpen}
                onClose={() => {
                    setDownloadPromptOpen(false);
                    setDownloadPromptRowKey(null);
                }}
                onMultipleDownload={handlePromptMultipleDownload}
                onSingleDownload={handlePromptSingleDownload}
            />
        </TableContainer>
        </Box>
    );
};
